import { useState, useEffect } from 'react';
import { 
  collection, 
  query, 
  onSnapshot, 
  orderBy, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  doc, 
  Timestamp,
  where,
  getDocs
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { toast } from 'react-toastify';
import { db, storage } from '../auth/firebase';
import { useAuth } from '../contexts/AuthContext';
import { HazardReport } from '../types';

export const useReports = () => {
  const [reports, setReports] = useState<HazardReport[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { currentUser } = useAuth();

  // Real-time subscription to reports
  useEffect(() => {
    const reportsQuery = query(
      collection(db, 'hazardReports'),
      orderBy('createdAt', 'desc')
    );

    const unsubscribe = onSnapshot(
      reportsQuery,
      (snapshot) => {
        try {
          const reportsData: HazardReport[] = [];
          snapshot.forEach((doc) => {
            const data = doc.data();
            reportsData.push({
              id: doc.id,
              title: data.title,
              description: data.description,
              hazardType: data.hazardType,
              latitude: data.latitude,
              longitude: data.longitude,
              photoUrl: data.photoUrl || '',
              userId: data.userId,
              userEmail: data.userEmail,
              createdAt: data.createdAt?.toDate() || new Date(),
              severity: data.severity,
              status: data.status || 'pending'
            });
          });
          
          setReports(reportsData);
          setError(null);
        } catch (err) {
          setError('Failed to load reports');
          
          // Provide fallback data
          setReports([
            {
              id: 'fallback-1',
              title: 'High Waves Alert',
              description: 'Dangerous wave conditions observed',
              hazardType: 'high-waves',
              latitude: 37.7749,
              longitude: -122.4194,
              photoUrl: '',
              userId: 'fallback-user',
              userEmail: 'user@example.com',
              createdAt: new Date(),
              severity: 'high',
              status: 'pending'
            }
          ]);
        } finally {
          setLoading(false);
        }
      },
      (err) => {
        setError('Failed to connect to reports');
        setLoading(false);
        
        // Provide fallback data
        setReports([]);
      }
    );

    return () => unsubscribe();
  }, []);

  // Narrow input for creating a new report (user fields/status are set internally)
  type NewReportInput = Pick<
    HazardReport,
    'title' | 'description' | 'hazardType' | 'latitude' | 'longitude' | 'severity' | 'photoUrl'
  > & { photoUrl?: string };

  // Add new report
  const addReport = async (reportData: NewReportInput) => {
    if (!currentUser) {
      throw new Error('User must be logged in to submit reports');
    }

    try {
      const docRef = await addDoc(collection(db, 'hazardReports'), {
        ...reportData,
        photoUrl: reportData.photoUrl || null, // Ensure photoUrl is not undefined
        userId: currentUser.uid,
        userEmail: currentUser.email || '',
        createdAt: Timestamp.now(),
        status: 'pending'
      });

      toast.success('Report submitted successfully!');
      return docRef.id;
    } catch (err) {
      console.error("Firestore Error: Failed to add report.", err);
      toast.error('Failed to submit report. Please check console for details.');
      throw new Error('Failed to add report');
    }
  };

  // Update report status
  const updateReportStatus = async (reportId: string, status: 'pending' | 'investigating' | 'resolved' | 'dismissed') => {
    if (!currentUser) {
      throw new Error('User must be logged in to update reports');
    }

    try {
      const reportRef = doc(db, 'hazardReports', reportId);
      await updateDoc(reportRef, {
        status,
        updatedAt: Timestamp.now()
      });
      
      toast.success('Report status updated successfully!');
    } catch (err) {
      toast.error('Failed to update report status');
      throw new Error('Failed to update report status');
    }
  };

  // Delete report
  const deleteReport = async (reportId: string) => {
    if (!currentUser) {
      throw new Error('User must be logged in to delete reports');
    }

    try {
      await deleteDoc(doc(db, 'hazardReports', reportId));
      toast.success('Report deleted successfully!');
    } catch (err) {
      console.error('Error deleting report:', err);
      toast.error('Failed to delete report.');
      throw new Error('Failed to delete report');
    }
  };

  // Upload photo to Firebase Storage
  const uploadReportPhoto = async (file: File): Promise<string> => {
    try {
      const storageRef = ref(storage, `hazard-reports/${Date.now()}_${file.name}`);
      const snapshot = await uploadBytes(storageRef, file);
      return await getDownloadURL(snapshot.ref);
    } catch (err) {
      throw new Error('Failed to upload photo');
    }
  };

  // Get reports by user
  const getUserReports = async (userId?: string): Promise<HazardReport[]> => {
    const targetUserId = userId || currentUser?.uid;
    if (!targetUserId) {
      return [];
    }

    try {
      const userReportsQuery = query(
        collection(db, 'hazardReports'),
        where('userId', '==', targetUserId),
        orderBy('createdAt', 'desc')
      );
      
      const snapshot = await getDocs(userReportsQuery);
      const userReports: HazardReport[] = [];
      
      snapshot.forEach((doc) => {
        const data = doc.data();
        userReports.push({
          id: doc.id,
          title: data.title,
          description: data.description,
          hazardType: data.hazardType,
          latitude: data.latitude,
          longitude: data.longitude,
          photoUrl: data.photoUrl || '',
          userId: data.userId,
          userEmail: data.userEmail,
          createdAt: data.createdAt?.toDate() || new Date(),
          severity: data.severity,
          status: data.status || 'pending'
        });
      });
      
      return userReports;
    } catch (err) {
      console.error('Error getting user reports:', err);
      return [];
    }
  };

  // Get reports statistics
  const getReportsStats = () => {
    const totalReports = reports.length;
    const activeReports = reports.filter(r => r.status === 'pending').length;
    const resolvedReports = reports.filter(r => r.status === 'resolved').length;
    const userReports = currentUser ? reports.filter(r => r.userId === currentUser.uid).length : 0;

    return {
      totalReports,
      activeReports,
      resolvedReports,
      userReports
    };
  };

  return {
    reports,
    loading,
    error,
    addReport,
    updateReportStatus,
    deleteReport,
    uploadReportPhoto,
    getUserReports,
    getReportsStats
  };
};
